<script lang="ts">
    import { useForm, router } from '@inertiajs/svelte';
    import { Button } from '@/components/ui/button';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import { formatDateLocale } from '@/lib/dateUtils';
    import { toast } from 'svelte-sonner';
    import { Pencil, Plus, Trash2 } from 'lucide-svelte';
    import type { PageProps, Comment } from '@/types/types';
    import * as Card from '@/components/ui/card';

    type Props = {
        commentableId: string;
        commentableType: string;
        comments: Comment[];
        currentUser?: PageProps['auth']['user'];
        reloadKey?: string;
        id?: string;
    };

    let { commentableId, commentableType, comments = [], currentUser, reloadKey = 'comments', id }: Props = $props();

    // Helper to deduplicate comments by ID
    function deduplicateComments(items: Comment[]): Comment[] {
        const seen = new Set<string>();
        return items.filter(c => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
        });
    }

    // Track the previous comments to detect prop changes
    let prevCommentsJson = $state(JSON.stringify(comments.map(c => c.id)));

    // Local state for comments - allows immediate updates without prop changes
    let localComments = $state<Comment[]>(deduplicateComments(comments));

    // Only sync from props when the comments actually change from the server
    // (not from our own local updates)
    $effect(() => {
        const currentCommentsJson = JSON.stringify(comments.map(c => c.id));
        if (currentCommentsJson !== prevCommentsJson) {
            const deduplicated = deduplicateComments(comments);
            localComments = deduplicated;
            prevCommentsJson = JSON.stringify(deduplicated.map(c => c.id));
        }
    });

    // Form for adding new comments
    let commentForm = $state(useForm({
        commentable_id: commentableId,
        commentable_type: commentableType,
        comment: '',
    }));

    // Edit state
    let editingCommentId = $state<string | null>(null);
    let editText = $state('');

    function submitComment() {
        if (!$commentForm.comment.trim()) return;

        $commentForm.post(route('comments.store'), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (response) => {
                // Access the flash data from the response
                const flashData = response.props.flash || {};

                if (flashData.comment) {
                    // Check if comment already exists (may have been synced via $effect)
                    const exists = localComments.some(c => c.id === flashData.comment.id);
                    if (!exists) {
                        localComments = [...localComments, flashData.comment];
                        // Update prevCommentsJson to prevent $effect from re-syncing
                        prevCommentsJson = JSON.stringify(localComments.map(c => c.id));
                    }

                    toast.success('Comment added successfully');
                    $commentForm.reset('comment');
                }
            },
            onError: () => {
                toast.error('Failed to add comment');
            }
        });
    }

    function startEdit(comment: Comment) {
        editingCommentId = comment.id;
        editText = comment.comment;
    }

    function cancelEdit() {
        editingCommentId = null;
        editText = '';
    }

    function saveEdit(commentId: string) {
        router.put(route('comments.update', { comment: commentId }), {
            comment: editText,
        }, {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (response) => {
                const flashData = response.props.flash || {};

                if (flashData.comment) {
                    localComments = localComments.map(item =>
                        item.id === commentId ? flashData.comment : item
                    );

                    toast.success('Comment updated successfully');
                    editingCommentId = null;
                    editText = '';
                }
            },
            onError: () => {
                toast.error('Failed to update comment');
            }
        });
    }

    function deleteComment(commentId: string) {
        if (!confirm('Are you sure you want to delete this comment?')) {
            return;
        }

        router.delete(route('comments.destroy', { comment: commentId }), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (response) => {
                const flashData = response.props.flash || {};

                if (flashData.id) {
                    localComments = localComments.filter(item => item.id !== flashData.id);
                    // Update prevCommentsJson to prevent $effect from re-syncing
                    prevCommentsJson = JSON.stringify(localComments.map(c => c.id));

                    toast.success('Comment deleted successfully');
                }
            },
            onError: () => {
                toast.error('Failed to delete comment');
            }
        });
    }

    function isOwnComment(comment: Comment): boolean {
        return currentUser?.id === comment.user.id;
    }
</script>
<Card.Root {id}>
    <Card.Content>
<div class="comments-section">
    <Card.Title class="flex items-center justify-between mb-2 text-lg">
        Comments ({localComments.length})
    </Card.Title>
    <!-- Add Comment Form -->
    <div class="add-comment-form">
        <Textarea
            bind:value={$commentForm.comment}
            label="Add a comment"
            error={$commentForm.errors.comment}
            className="m-0"
        />
        <div class="form-actions">
            <Button
                onclick={submitComment}
                disabled={$commentForm.processing || !$commentForm.comment.trim()}
            >
                {$commentForm.processing ? 'Posting...' : 'Post Comment'}
            </Button>
        </div>
    </div>

    <!-- Comments List -->
    <div class="comments-list">
        {#if localComments.length === 0}
            <p class="no-comments">No comments yet. Be the first to comment!</p>
        {:else}
            {#each localComments as comment (comment.id)}
                <div class="comment-item">
                    <div class="comment-header">
                        <div class="comment-meta">
                            <span class="comment-author">{comment.user.name}</span>
                            <span class="comment-date">{formatDateLocale(comment.created_at)}</span>
                        </div>
                        {#if isOwnComment(comment) && editingCommentId !== comment.id}
                            <div class="comment-actions">
                                <button
                                    class="action-icon-btn edit-btn"
                                    onclick={() => startEdit(comment)}
                                    aria-label="Edit comment"
                                >
                                    <Pencil size={20} />
                                </button>
                                <button
                                    class="action-icon-btn delete-btn"
                                    onclick={() => deleteComment(comment.id)}
                                    aria-label="Delete comment"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                        {/if}
                    </div>

                    <div class="comment-body">
                        {#if editingCommentId === comment.id}
                            <textarea
                                bind:value={editText}
                                class="edit-textarea"
                                rows="3"
                            ></textarea>
                            <div class="edit-actions">
                                <Button size="sm" onclick={() => saveEdit(comment.id)}>Save</Button>
                                <Button size="sm" variant="outline" onclick={cancelEdit}>Cancel</Button>
                            </div>
                        {:else}
                            <p class="comment-text">{comment.comment}</p>
                        {/if}
                    </div>
                </div>
            {/each}
        {/if}
    </div>
</div>
    </Card.Content>
</Card.Root>

<style lang="scss">
    .comments-section {
        background: var(--clr-surface-2);
        border-radius: 8px;
        border: 1px solid var(--clr-border);
    }

    .comments-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 1.5rem;
        color: var(--clr-on-surface-1);
    }

    .add-comment-form {
        padding-bottom: 1.5rem;
        border-bottom: 1px solid var(--clr-border);
    }

    .form-actions {
        margin-top: 0.75rem;
        display: flex;
        justify-content: flex-end;
    }

    .comments-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .no-comments {
        text-align: center;
        color: var(--clr-on-surface-3);
        padding: 2rem 0;
        font-style: italic;
    }

    .comment-item {
        background: var(--clr-surface-1);
        border-radius: 6px;
        padding: 1.5rem;
        border: 1px solid var(--border);
    }

    .comment-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 0.75rem;
    }

    .comment-meta {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .comment-author {
        font-weight: 600;
        color: var(--clr-on-surface-1);
    }

    .comment-date {
        font-size: 0.875rem;
        color: var(--clr-on-surface-3);
    }

    .comment-body {
        margin-top: 0.5rem;
    }

    .comment-text {
        color: var(--clr-on-surface-2);
        white-space: pre-wrap;
        word-break: break-word;
        margin: 0;
    }

    .comment-actions {
        display: flex;
        gap: 0.25rem;
        margin: 0;
    }

    .action-icon-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem;
        border: none;
        background: transparent;
        border-radius: 4px;
        cursor: pointer;
        transition: all 200ms;
        color: var(--clr-on-surface-3);

        &:hover {
            background: var(--clr-surface-3);
        }
    }

    .edit-btn {
        &:hover {
            color: var(--chart-2);
        }
    }

    .delete-btn {
        &:hover {
            color: var(--destructive);
        }
    }

    .edit-actions {
        display: flex;
        gap: 0.5rem;
        margin-top: 0.75rem;
    }

    .edit-textarea {
        width: 100%;
        padding: 0.5rem 0.75rem;
        background: var(--clr-surface-2);
        border: 1px solid var(--clr-border);
        border-radius: 6px;
        color: var(--clr-on-surface-1);
        font-size: 0.875rem;
        font-family: inherit;
        resize: vertical;
        transition: border-color 200ms;

        &:focus {
            outline: none;
            border-color: var(--primary);
        }
    }
</style>
